/* ──────────────────────────────────────────────────────────────────────────
   dataDisclosures.js — third-party tracker & PII-disclosure detection
   ---------------------------------------------------------------------------
   Pure, dependency-free detection logic shared by the live Disclosures tab and
   any offline report. Ports the verified HAR analyzer (qa-captures report).

   detectDisclosures(row) inspects a single network row (live or HAR-derived):
     • query params on row.url
     • OpenRTB / Amazon-DTB JSON in row.requestBody
     • request headers (cookie / user-agent / referer)
   and returns an array of flags: { type, severity, param, value, scope }.

   summarize(rows) aggregates flags into vendor inventory, a vendor×type
   matrix, identity-token sources, and rule-based findings (F1…F6).

   Severities: 'high' (identifier/PII) · 'med' (pseudonymous id) ·
               'consent' (privacy signal) · 'low' (context/device)
   ────────────────────────────────────────────────────────────────────────── */

export const SEVERITY_ORDER = ['high', 'med', 'consent', 'low'];
export const SEVERITY_RANK = { high: 3, med: 2, consent: 1, low: 0 };
export const SEVERITY_COLOR = {
  high: '#e5484d', med: '#f5a524', consent: '#3e63dd', low: '#8b8d98',
};
export const SEVERITY_LABEL = {
  high: 'Identifier / PII', med: 'Pseudonymous ID',
  consent: 'Consent signal', low: 'Context / device',
};

/* ── Vendor taxonomy: first matching host substring wins ────────────────── */
const VENDORS = [
  ['doubleverify.com', 'DoubleVerify', 'Verification / Viewability'],
  ['adsafeprotected.com', 'IAS', 'Verification / Viewability'],
  ['moatads.com', 'Oracle MOAT', 'Verification / Viewability'],
  ['doubleclick.net', 'Google GAM / DoubleClick', 'Ad Server / Exchange'],
  ['googlesyndication.com', 'Google GAM', 'Ad Server / Exchange'],
  ['amazon-adsystem.com', 'Amazon APS', 'Header-bidding Exchange'],
  ['adnxs.com', 'Xandr / AppNexus', 'Header-bidding SSP'],
  ['adnxs-simple.com', 'Xandr / AppNexus', 'Header-bidding SSP'],
  ['springserve.com', 'SpringServe', 'Video Ad Server / SSP'],
  ['rubiconproject.com', 'Magnite (Rubicon)', 'Header-bidding SSP'],
  ['magnite.com', 'Magnite', 'Header-bidding SSP'],
  ['pubmatic.com', 'PubMatic', 'Header-bidding SSP'],
  ['casalemedia.com', 'Index Exchange', 'Header-bidding SSP'],
  ['indexww.com', 'Index Exchange', 'Header-bidding SSP'],
  ['openx.net', 'OpenX', 'Header-bidding SSP'],
  ['3lift.com', 'TripleLift', 'Header-bidding SSP'],
  ['smartadserver.com', 'Smart AdServer', 'Header-bidding SSP'],
  ['spotx', 'SpotX / Magnite', 'Video Ad Exchange'],
  ['teads.tv', 'Teads', 'Video Ad Exchange'],
  ['criteo.', 'Criteo', 'Retargeting / DSP'],
  ['taboola.com', 'Taboola', 'Native / Content Rec'],
  ['outbrain.com', 'Outbrain', 'Native / Content Rec'],
  ['scorecardresearch.com', 'Comscore', 'Measurement / Analytics'],
  ['imrworldwide.com', 'Nielsen', 'Measurement / Analytics'],
  ['liadm.com', 'LiveIntent', 'Identity / Email'],
  ['rlcdn.com', 'LiveRamp', 'Identity Resolution'],
  ['liveramp.com', 'LiveRamp', 'Identity Resolution'],
  ['id5-sync.com', 'ID5', 'Identity Resolution'],
  ['crwdcntrl.net', 'Lotame', 'DMP / Data'],
  ['demdex.net', 'Adobe Audience Mgr', 'DMP / Data'],
  // App-specific first-party / contextual hosts are not hardcoded — they come
  // from the active App Pack (contentApis). Only cross-publisher third-party
  // vendors are listed here.
];

const FIRST_PARTY_CATS = new Set(['First-party Content', 'First-party CDN']);

export function classifyVendor(host = '') {
  const h = String(host).toLowerCase();
  for (const [sub, vendor, cat] of VENDORS) {
    if (h.includes(sub)) return { vendor, category: cat };
  }
  return { vendor: host || 'unknown', category: 'Unclassified' };
}

/* ── Query-param signals (name-based, conservative) ─────────────────────── */
const PARAM_SIGNALS = [
  [/^(rdid|idfa|ifa|aaid|gaid)$/i, 'Advertising ID (IDFA/GAID)', 'high'],
  [/^ppid$/i, 'Publisher-provided ID (PPID)', 'med'],
  [/^(us_privacy|usp)$/i, 'US Privacy (CCPA) string', 'consent'],
  [/^gpp$/i, 'GPP string', 'consent'],
  [/^gpp_sid$/i, 'GPP section IDs', 'consent'],
  [/^gdpr$/i, 'GDPR-applies flag', 'consent'],
  [/^(gdpr_consent|euconsent)$/i, 'TCF consent string', 'consent'],
  [/^(attmas|att|atts)$/i, 'ATT status', 'consent'],
  [/^(is_lat|lmt|dnt)$/i, 'Limit-Ad-Tracking flag', 'consent'],
  [/^adid_p$/i, 'Personalized-ads flag', 'consent'],
  [/^(url|content_url|page_url|loc)$/i, 'Page/Content URL', 'low'],
  [/^(platform|os|osv|make|model|hwv)$/i, 'Device make/model/OS', 'low'],
  [/^(x-api-key|api_key|apikey)$/i, 'API key (in URL)', 'med'],
];

const ZERO_RX = /^[0\- ]+$/;
const isRealIfa = (v) => !!v && !ZERO_RX.test(String(v));

function clip(v, n = 70) {
  const s = v == null ? '' : String(v);
  return s.length <= n ? s : s.slice(0, n) + '…';
}

/* Normalize headers (array-of-{name,value} or plain object) → lowercase map */
function headerMap(h) {
  const out = {};
  if (!h) return out;
  if (Array.isArray(h)) {
    h.forEach((x) => {
      const k = (x?.name || x?.key || '').toLowerCase();
      if (k) out[k] = x?.value ?? x?.val ?? '';
    });
  } else if (typeof h === 'object') {
    Object.keys(h).forEach((k) => { out[k.toLowerCase()] = h[k]; });
  }
  return out;
}

function asObject(body) {
  if (!body) return null;
  if (typeof body === 'object') return body;
  if (typeof body === 'string') { try { return JSON.parse(body); } catch { return null; } }
  return null;
}

/* ── OpenRTB bid body (AppNexus / SpringServe / generic prebid) ─────────── */
function parseOpenRtb(j, push) {
  const dev = j.device || {};
  if ('ifa' in dev) {
    if (isRealIfa(dev.ifa)) push('Advertising ID (IDFA/GAID)', 'high', 'device.ifa', dev.ifa);
    else push('Zeroed/blank IDFA (LAT honored)', 'low', 'device.ifa', dev.ifa);
  }
  if ('lmt' in dev) push('Limit-Ad-Tracking flag', 'consent', 'device.lmt', dev.lmt);
  if (dev.ext && 'atts' in dev.ext) push('ATT status', 'consent', 'device.ext.atts', dev.ext.atts);
  if (dev.geo) push('Geo / location', 'high', 'device.geo', JSON.stringify(dev.geo).slice(0, 60));
  if (dev.ip && String(dev.ip).trim()) push('IP address', 'high', 'device.ip', dev.ip);
  if (dev.make || dev.model || dev.hwv || dev.os || dev.osv) {
    push('Device make/model/OS', 'low', 'device.*',
      `${dev.make || ''} ${dev.model || ''} ${dev.os || ''} ${dev.osv || ''} ${dev.hwv || ''}`.trim());
  }
  if (dev.ua) push('User-Agent', 'low', 'device.ua', dev.ua);

  const usr = j.user || {};
  const ext = (usr.ext && typeof usr.ext === 'object') ? usr.ext : {};
  const eids = ext.eids || [];
  if (Array.isArray(eids) && eids.length) {
    const srcs = new Set();
    eids.forEach((e) => {
      (e.uids || []).forEach((u) => srcs.add((u.ext && u.ext.rtiPartner) || e.source || '?'));
      if (!(e.uids || []).length) srcs.add(e.source || '?');
    });
    push('Identity-graph token (eids)', 'high', 'user.ext.eids', [...srcs].sort().join(', '));
  }
  if ('consent' in ext) push('TCF consent string', 'consent', 'user.ext.consent', ext.consent ? ext.consent : '(EMPTY)');
  if (usr.id) push('User ID', 'med', 'user.id', usr.id);
  if (usr.buyeruid) push('Buyer UID', 'med', 'user.buyeruid', usr.buyeruid);

  const regs = j.regs || {};
  const rext = (regs.ext && typeof regs.ext === 'object') ? regs.ext : {};
  if ('us_privacy' in rext) push('US Privacy (CCPA) string', 'consent', 'regs.ext.us_privacy', rext.us_privacy);
  if ('us_privacy' in regs) push('US Privacy (CCPA) string', 'consent', 'regs.us_privacy', regs.us_privacy);
  if ('gdpr' in rext) push('GDPR-applies flag', 'consent', 'regs.ext.gdpr', rext.gdpr);
  if ('gdpr' in regs) push('GDPR-applies flag', 'consent', 'regs.gdpr', regs.gdpr);
  if ('gpp' in regs) push('GPP string', 'consent', 'regs.gpp', regs.gpp ? regs.gpp : '(EMPTY)');
  if ('gpp' in rext) push('GPP string', 'consent', 'regs.ext.gpp', rext.gpp ? rext.gpp : '(EMPTY)');

  const app = j.app || {};
  if (app.bundle || app.storeurl) push('App identity (bundle/store)', 'low', 'app.bundle', app.bundle || app.storeurl);
  const cu = (app.content && app.content.url) || app.domain;
  if (cu) push('Page/Content URL', 'low', 'app.content.url', cu);
}

/* ── Amazon DTB body (proprietary header-bidding schema) ────────────────── */
function parseAmazon(j, push) {
  if ('idfa' in j) {
    if (isRealIfa(j.idfa)) push('Advertising ID (IDFA/GAID)', 'high', 'idfa', j.idfa);
    else push('Zeroed/blank IDFA (LAT honored)', 'low', 'idfa', j.idfa);
  }
  if (j['ad-id']) push('Amazon device ad-id', 'med', 'ad-id', j['ad-id']);
  if ('oo' in j) push('Opt-out flag', 'consent', 'oo', j.oo);
  if ('fcoo' in j) push('Opt-out flag', 'consent', 'fcoo', j.fcoo);
  if ('us_privacy' in j) push('US Privacy (CCPA) string', 'consent', 'us_privacy', j.us_privacy);
  const g = j.gdpr;
  if (g && typeof g === 'object') push('TCF consent string', 'consent', 'gdpr.c', g.c ? g.c : '(EMPTY)');
  else if (g != null) push('GDPR-applies flag', 'consent', 'gdpr', g);
  const regs = j.regs || {};
  if ('gpp' in regs) push('GPP string', 'consent', 'regs.gpp', regs.gpp ? regs.gpp : '(EMPTY)');
  if (j.ua) push('User-Agent', 'low', 'ua', j.ua);
  if (j.appId) push('App identity (bundle/store)', 'low', 'appId', j.appId);
}

const looksAmazon = (j) => j && ('idfa' in j || 'isDTBMobile' in j || 'ad-id' in j);

/* ── Per-request detection ──────────────────────────────────────────────── */
export function detectDisclosures(row = {}) {
  const url = row.url || '';
  let host = '';
  try { host = new URL(url).host; } catch { host = ''; }
  const { vendor, category } = classifyVendor(host);

  const flags = [];
  const seen = new Set();
  const push = (type, severity, param, value) => {
    if (seen.has(type)) return;       // one flag per type per request
    seen.add(type);
    flags.push({ type, severity, param, value: clip(value) });
  };

  // 1) query params
  try {
    const sp = new URL(url).searchParams;
    for (const [name, value] of sp.entries()) {
      for (const [rx, type, sev] of PARAM_SIGNALS) {
        if (rx.test(name)) push(type, sev, name, value);
      }
    }
  } catch { /* non-URL */ }

  // 2) request body (OpenRTB / Amazon DTB)
  const body = asObject(row.requestBody);
  if (body) {
    if (looksAmazon(body)) parseAmazon(body, push);
    else parseOpenRtb(body, push);
  }

  // 3) headers
  const H = headerMap(row.requestHeaders);
  if (H['user-agent']) push('User-Agent', 'low', 'User-Agent header', H['user-agent']);
  if (H['referer']) push('Referer (page URL)', 'low', 'Referer', H['referer']);
  if (H['cookie']) push('Cookies sent', 'med', 'Cookie header', H['cookie']);

  const topSeverity = flags.reduce(
    (acc, f) => (SEVERITY_RANK[f.severity] > SEVERITY_RANK[acc] ? f.severity : acc),
    'low',
  );

  return {
    host, vendor, category, flags,
    isFirstParty: FIRST_PARTY_CATS.has(category),
    topSeverity: flags.length ? topSeverity : null,
    // convenience booleans used by findings:
    hasIdentifier: flags.some((f) => f.severity === 'high'),
    hasConsentSignal: flags.some((f) => f.severity === 'consent'),
    emptyGpp: flags.some((f) => f.type === 'GPP string' && f.value === '(EMPTY)'),
    emptyTcf: flags.some((f) => f.type === 'TCF consent string' && f.value === '(EMPTY)'),
  };
}

/* ── Aggregate many rows into inventory + matrix + findings ─────────────── */
export function summarize(rows = []) {
  const vendors = new Map();
  const dataTypeTotals = {};
  const tokenSources = {};
  let scanned = 0;
  let flaggedCount = 0;

  const ex = {}; // type → first concrete example (for findings)

  rows.forEach((row) => {
    scanned += 1;
    const d = detectDisclosures(row);
    if (!d.flags.length) return;
    flaggedCount += 1;

    const v = vendors.get(d.vendor) || {
      vendor: d.vendor, category: d.category, hosts: new Set(),
      requests: 0, dataTypes: {}, topSeverity: 'low',
    };
    v.hosts.add(d.host);
    v.requests += 1;
    if (SEVERITY_RANK[d.topSeverity] > SEVERITY_RANK[v.topSeverity]) v.topSeverity = d.topSeverity;

    d.flags.forEach((f) => {
      v.dataTypes[f.type] = (v.dataTypes[f.type] || 0) + 1;
      dataTypeTotals[f.type] = (dataTypeTotals[f.type] || 0) + 1;
      if (f.type === 'Identity-graph token (eids)') {
        f.value.split(', ').forEach((s) => { if (s) tokenSources[s] = (tokenSources[s] || 0) + 1; });
      }
      if (!ex[f.type] && !d.isFirstParty) {
        ex[f.type] = {
          vendor: d.vendor, host: d.host, param: f.param, value: f.value,
          url: clip(row.url, 160), type: f.type,
          requestType: row.requestType || row.type || '',
        };
      }
    });
    vendors.set(d.vendor, v);
  });

  const vendorList = [...vendors.values()]
    .map((v) => ({ ...v, hosts: [...v.hosts] }))
    .sort((a, b) => b.requests - a.requests);

  const nonFirstParty = (type) => [...new Set(
    vendorList.filter((v) => v.dataTypes[type] && !FIRST_PARTY_CATS.has(v.category)).map((v) => v.vendor),
  )];
  const emptyVendors = (type) => [...new Set(
    rows.map(detectDisclosures)
      .filter((d) => d.flags.some((f) => f.type === type && f.value === '(EMPTY)'))
      .map((d) => d.vendor),
  )];

  const findings = [
    {
      id: 'F1', severity: 'High',
      title: 'Device advertising ID (IDFA/GAID) transmitted in cleartext to SSPs',
      detail: 'The raw device advertising identifier is sent unencrypted in the bid-request body to header-bidding partners. Cross-partner correlation of a single user is trivial.',
      vendors: nonFirstParty('Advertising ID (IDFA/GAID)'),
      example: ex['Advertising ID (IDFA/GAID)'] || null,
    },
    {
      id: 'F2', severity: 'High',
      title: 'Persistent identity-graph tokens (UID2 / LiveRamp) shared with exchanges',
      detail: `OpenRTB user.ext.eids carries cross-site identity tokens. Sources: ${Object.keys(tokenSources).join(', ') || '—'}.`,
      vendors: nonFirstParty('Identity-graph token (eids)'),
      example: ex['Identity-graph token (eids)'] || null,
    },
    {
      id: 'F3', severity: 'High',
      title: 'GPP consent string sent EMPTY while identifiers flow',
      detail: 'The GPP (Global Privacy Platform) field is present but blank on these partners while IDFA / identity tokens are included. A blank GPP cannot communicate the user\'s privacy choices downstream.',
      vendors: emptyVendors('GPP string'),
      example: ex['GPP string'] || null,
    },
    {
      id: 'F4', severity: 'Medium',
      title: 'TCF consent string EMPTY on requests carrying identifiers',
      detail: 'user.ext.consent / gdpr.c is blank. Acceptable for a US-only (GDPR=0) session, but no positive consent signal accompanies the data if resolved in a TCF region.',
      vendors: emptyVendors('TCF consent string'),
      example: ex['TCF consent string'] || null,
    },
    {
      id: 'F5', severity: 'Medium',
      title: 'Full page/content URL (browsing activity) disclosed to demand partners',
      detail: 'The exact article/video URL being viewed is sent to bidders, exposing reading habits alongside the device ID.',
      vendors: nonFirstParty('Page/Content URL'),
      example: ex['Page/Content URL'] || null,
    },
    {
      id: 'F6', severity: 'Low',
      title: 'Contextual API key exposed in cleartext request URL',
      detail: 'A read-only contextual endpoint passes its api key as a plain query parameter, harvestable from any proxy.',
      vendors: nonFirstParty('API key (in URL)'),
      example: ex['API key (in URL)'] || null,
    },
  ].filter((f) => f.vendors.length); // only surface findings that actually fired

  return {
    meta: { scanned, flagged: flaggedCount, vendors: vendorList.length },
    vendors: vendorList,
    dataTypeTotals,
    tokenSources,
    findings,
  };
}

/* Ordered data-type list for matrix columns (stable across renders) */
export const MATRIX_TYPES = [
  ['Adv. ID', 'Advertising ID (IDFA/GAID)', 'high'],
  ['Identity tok.', 'Identity-graph token (eids)', 'high'],
  ['Amzn ad-id', 'Amazon device ad-id', 'med'],
  ['PPID', 'Publisher-provided ID (PPID)', 'med'],
  ['Device', 'Device make/model/OS', 'low'],
  ['UA', 'User-Agent', 'low'],
  ['Page URL', 'Page/Content URL', 'low'],
  ['Cookies', 'Cookies sent', 'med'],
  ['US Priv', 'US Privacy (CCPA) string', 'consent'],
  ['GPP', 'GPP string', 'consent'],
  ['TCF', 'TCF consent string', 'consent'],
  ['ATT', 'ATT status', 'consent'],
];
